import { Injectable } from '@angular/core';
import * as _ from 'lodash';

@Injectable()
export class WikiHelperService {
    constructor() { }

    isEmail(url: string): boolean {
        const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/gi;
        return !!url.match(emailRegex);
    }

    getContents(wikiContent: any): string {
        return wikiContent.data || '<p>There is no content for this wiki page yet</p>';
    }

    convertWikiWords(content: string, urlFn: any): string {
        const regex = /(?<!\\)\[([^\[\]\|]+)(?:\|([^\[\]]+))?\](?!\()/gm;
        const regexRejectWord = /[^a-zA-Z0-9 _\-,.():]/g;
        const formatter = (match, p1, p2) => {
            // Does the potential page name include any illegal chars?  Reject.
            if (p1.match(regexRejectWord)) {
                return match;
            }
            const url = urlFn(p1).trim();
            const title = (p2 ?? p1).trim();
            return `<a href="${ url }" title="${ title }">${ title }</a>`;
        };
        // Wiki-word bracket syntax ([PageName]) and Mermaid's node-label bracket
        // syntax ([Label]) are otherwise indistinguishable to this regex — skip
        // <pre>/<code>/<a> regions so fenced code (Mermaid diagrams especially)
        // doesn't get [ ]-delimited text spliced into <a> tags, which consumes the
        // delimiters Mermaid's own grammar requires.
        return this.replaceOutsideCodeRegions(content, (segment) => segment.replace(regex, formatter));
    }

    private replaceOutsideCodeRegions(html: string, transform: (segment: string) => string): string {
        const protectedRe = /<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>|<a\b[\s\S]*?<\/a>/gi;
        let out = '';
        let last = 0;
        let m: RegExpExecArray | null;
        while ((m = protectedRe.exec(html)) !== null) {
            out += transform(html.slice(last, m.index));
            out += m[0];
            last = protectedRe.lastIndex;
        }
        out += transform(html.slice(last));
        return out;
    }

    getLinks(wikiPage: string, internalUrlBase: string): any {
        var internal = [], external = [];

        const regex = /<a\s+[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi;

        var match;
        while ((match = regex.exec(wikiPage)) !== null) {
            const url = match[1];
            const pagename = match[2];

            const link = {
                id: pagename + "_" + url,
                title: pagename,
                url: url
            };

            if (url.startsWith(internalUrlBase)) {
                internal.push(link);
            } else if (! this.isEmail(url)) {
                try {
                    const { hostname } = new URL(url, window.location.origin);
                    link.title = `${ pagename } (${ hostname })`;
                    external.push(link);
                } catch (_) {
                    // Skip malformed or relative-only URLs (e.g. "#anchor")
                }
            }
        }

        return {
            internal: _.uniqBy(internal, 'id'),
            external: _.uniqBy(external, 'id')
        };
    }

    toEditable(wikiPage: string): string {
        return wikiPage.replace(/(>[^<]*?)\[/g, function (match, pre) {
            return pre + "\\[";
        }).replace(/<u>([^<]+?)<\/u>[\s\n\r]*<a href context-menu="setLinkUrl\('(.+?)'\)" data-target="wiki-link-menu" data-pagename="(.+?)">[\s\n\r]*\?[\s\n\r]*<\/a>/g, function (match, p1, p2, p3) {
            p3 = decodeURIComponent(p3);
            if (p1 !== p3) {
                return "[" + p3 + " | " + p1 + "]";
            }
            else {
                return "[" + p1 + "]";
            }
        }).replace(/<a href context-menu="setLinkUrl\('(.+?)'\)" data-target="wiki-link-menu" data-pagename="(.+?)">[\s\n\r]*<u>([^<]+?)<\/u>[\s\n\r]*<\/a>/g, function (match, p1, p2, p3) {
            p1 = decodeURIComponent(p1);
            if (p1 !== p3) {
                return "[" + p1 + " | " + p3 + "]";
            }
            else {
                return "[" + p3 + "]";
            }
        }).replace(/(<a([^>]+))>(.+?)(<\/a>)|(<\s*.[^>]*>)|([^<]*?)((\\)?\[[^\]]*?)?([\w\[])?(?![^[]*])([A-Z][a-z]+([A-Z][a-z]+)+)/g, function (match, x1, x2, x3, x4, x5, pre, p1, esc, p2, p3, p4, offset, original) {
            // This regexp catches the following:
            //  .Anchor tags: <a...>....</a>
            //  .Tag contents (tag and its attributes): <img src="..." />
            //  .Camel case text
            //
            // For the first two cases, it just returns the original matched substring, so it doesn't get any changes
            // For the CamelCase, that is not in an anchor nor in a tag property, it applies the transformation

            // The x1 and x5 are the original strings for the first two types of match, since we don't want to make any changes
            // to them, we just return them as-is
            // Notice:
            //  We don't want to escape ("\") camel case strings that are enclosed between square brackets,
            //  for that we added the following negative look ahead section: (?![^[]*])

            if (x1 || x5) {
                return match;
            }

            if (esc) {
                return pre + p1 + (p2 ? p2 : '') + "\\" + p3;
            }
            return (p1 || p2) ? match : pre + "\\" + p3;
        });

    }

    /**
     * Add `id` attributes to <h1>-<h6> tags so URL fragments can target them.
     * Lowercased + kebab-cased to match what users type (`#purge-script`).
     * Duplicate ids get `-2`, `-3` suffixes. Unlike processPage, no brace-escaping or legacy URL rewriting.
     */
    addHeadingIds(html: string): string {
        const seen: { [id: string]: number } = {};
        return html.replace(
            /<h([123456])([^>]*?)>((<[^>]+?>)*?([^<>]+?)(<[^>]+?>)*?)<\/h([123456])>/g,
            (match, p1, _p2, p3, _p4, p5) => {
                const base = p5.toLowerCase().replace(/ /g, '-').replace(/[!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, '');
                if (!base) return match;
                const count = (seen[base] || 0) + 1;
                seen[base] = count;
                const id = count === 1 ? base : `${base}-${count}`;
                return `<h${p1} id="${id}">${p3}</h${p1}>`;
            }
        );
    }

    processPage(html: string, allowWikiLinks: boolean): string {
        if (allowWikiLinks === false) {
            html = html
                .replace(/<u>([^<]+?)<\/u>[\s\n\r]*<a href=['"]\/gf\/project\/(.*?)\/wiki\/\?(.+&)?pagename=(.+?)(&.+?)?['"]>[\s\n\r]*\?[\s\n\r]*<\/a>/g, function (match, pageTitle, unixName, queryPrefix, pageName, querySuffix) {
                    pageName = decodeURIComponent(pageName.replace(/\+/g, " "));
                    if (pageTitle !== pageName) {
                        return "[" + decodeURIComponent(pageName) + " | " + pageTitle + "]";
                    } else {
                        return "[" + pageTitle + "]";
                    }
                })
                .replace(/<a href=['"]\/gf\/project\/(.*)?\/wiki\/\?(.+&)?pagename=(.+?)(&.+?)?['"]>[\s\n\r]*([^<]+?)[\s\n\r]*<\/a>/g, function (match, unixName, queryPrefix, pageName, querySuffix, pageTitle) {
                    pageName = decodeURIComponent(pageName.replace(/\+/g, " "));
                    if (pageTitle !== pageName) {
                        return "[" + decodeURIComponent(pageName) + " | " + pageTitle + "]";
                    } else {
                        return "[" + pageTitle + "]";
                    }
                });
        } else {
            html = html.replace(/href=['"]\/gf\/project\/(.*?)\/wiki\/\?(.+&)?pagename=(.+?)(&.+?)?['"]/g, function (match, p1, p2, p3, p4) {
                var pageName = (decodeURIComponent(p3.replace(/\+/g, ' '))).replace(/\'/g, '\\\'');
                return 'href context-menu="' + pageName + '" data-target="wiki-link-menu" data-pagename="' + pageName + '"';
            });
        }

        return html
            .replace(/['"]\/ckeditor\/filemanager\/img.php\?file=(\d+)['"]/g, '"/api/filesystem/$1"')
            .replace(/([\{\}])/g, "{{ '$1' }}")  // Escape { and } chars, avoid Angular compile errors
            .replace(/<h([123456])([^>]*?)>((<[^>]+?>)*?([^<>]+?)(<[^>]+?>)*?)<\/h([123456])>/g, function (match, p1, p2, p3, p4, p5) {
                // Lowercase + kebab-case so URL fragments like #purge-script match what users naturally type.
                var id = p5.toLowerCase().replace(/ /g, "-").replace(/[!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, "");
                return '<h' + p1 + ' id="' + id + '">' + p3 + '</h' + p1 + '>';
            });
    }

    wikiToTitle(wikiWords: string): string {
        return wikiWords
            // insert a space before all caps
            .replace(/([A-Z])/g, ' $1')
            // uppercase the first character
            .replace(/^./, function (str) { return str.toUpperCase(); })
            .trim();  // Remove leading and trailing spaces
    }

    titleToWiki(regularTitle) {
        return regularTitle.replace(/\W+(.)/g,
            function (match, chr) { return chr.toUpperCase(); }
        );
    }

    getMappedListValues(map, list) {
        return _.filter(_.map(list, function (item) {
            return _.get(map, item, null);
        }));
    }

    getToolbarMap() {
        return {
            "strong": "bold",
            "em": "italic",
            "u": "underline",
            "strike": "strikethrough",
            "a": "link",
            "ul": "bullist",
            "ol": "numlist",
            "sub": "subscript",
            "sup": "superscript",
            "img": "img",
            "table": "table",
        };
    }

    getToolbarNames(allowedList) {
        return this.getMappedListValues(
            this.getToolbarMap(),
            allowedList.split(',')
        ).toString();
    }

    getFormatMap() {
        return {
            "p": "Paragraph=p;",
            "h1": "Header 1=h1;",
            "h2": "Header 2=h2;",
            "h3": "Header 3=h3;",
            "h4": "Header 4=h4;",
            "h5": "Header 5=h5;",
            "h6": "Header 6=h6;",
            "span": "Span=span;",
            "pre": "Pre=pre;",
            "div": "Div=div;",
            "address": "Address=address;",
        }
    }

    getFormats(allowedList) {
        return this.getMappedListValues(
            this.getFormatMap(),
            allowedList.split(',')
        ).join('');  // No commas in this output
    }

    getRequiredPluginMap() {
        return {
            "hr": "hr",
            "a": "link",
            "table": "table"
        };
    }

    getRequiredPlugins(allowedList) {
        return this.getMappedListValues(
            this.getRequiredPluginMap(),
            allowedList.split(',')
        ).toString();
    }
}
